<template>
    <div class="form-container">
        <AddressField
            :name="name"
            :label="label"
            :searchName="searchName"
            :searchLabel="searchLabel"
            :addressFields="addressFields"
            :customErrorMessage="errorMessage"
            :validationRules="validationRules"
        />
    </div>
</template>

<script>
import { useStore } from 'vuex'

export default {
    // static settings to initialize step (not reactive inside this file!)
    stepDefinition() {
        return {
            name: 'employerAddress',
            hideContinue: true,
            stringOnSummary: true,
            selected: null,
            manual: true,
            route: {
                relativeUrl: process.env.TERRITORY === 'pol' ? `numer-mieszkania` : `employer-address`,
                name: 'apply.address.employer',
                props: { addressType: 'employer', formSection: 'employment' },
                meta: {
                    title: 'form.sections.address.employerAddress.heading',
                },
            },
            fields: {
                employerAddressBuildingName: {
                    value: null,
                    mappedKey: 'employer_street_no',
                    validationRules: {
                        required: true,
                    },
                },
                employerAddressBuildingNumber: {
                    value: null,
                    mappedKey: 'employer_street_house_no',
                },
                employerAddressCity: {
                    value: null,
                    mappedKey: 'employer_city',
                    validationRules: {
                        required: true,
                        max: 50,
                    },
                },
                employerAddressStreetName: {
                    value: null,
                    mappedKey: 'employer_street_name',
                    validationRules: {
                        required: true,
                        min: 2,
                        max: 50,
                    },
                },
                employerAddressPostcode: {
                    value: null,
                    mappedKey: 'employer_postal_code',
                    mask: '##-###',
                    validationRules: {
                        required: true,
                        min: 6,
                        max: 6,
                    },
                },
            },
        }
    },

    setup() {
        const store = useStore()

        return {
            store,
        }
    },
    created() {
        this.emitter.emit('stepLoaded')

        this.addressFields.Postcode.validationValue = this.postcodeValue
        this.addressFields.BuildingName.validationValue = this.buildingNameValue
        this.addressFields.BuildingNumber.validationValue = this.buildingNumberValue
        this.addressFields.StreetName.validationValue = this.streetNameValue
        this.addressFields.City.validationValue = this.cityValue

        this.addressFields.Postcode.validationRules = this.store.getters.fieldValidationRules(`${this.addressType}AddressPostcode`)
        this.addressFields.BuildingName.validationRules = this.store.getters.fieldValidationRules(`${this.addressType}AddressBuildingName`)
        this.addressFields.BuildingNumber.validationRules = this.store.getters.fieldValidationRules(`${this.addressType}AddressBuildingNumber`)
        this.addressFields.StreetName.validationRules = this.store.getters.fieldValidationRules(`${this.addressType}AddressStreetName`)
        this.addressFields.City.validationRules = this.store.getters.fieldValidationRules(`${this.addressType}AddressCity`)

        this.emitter.all.delete('addressField:selected')
        this.emitter.on('addressField:selected', () => {
            this.emitter.emit('nextStep')
        })
    },
    props: ['addressType', 'formSection'],
    data() {
        return {
            name: `${this.addressType}Address`,
            label: this.$t(`form.sections.address.${this.addressType}Address.label`),
            errorMessage: this.$t('form.fields.postcode.errorMessage'),
            validationRules: {
                required: true,
            },
            tooltip: null,
            searchName: `${this.addressType}AddressPostcode`,
            searchLabel: this.$t('form.fields.address.labels.postcode'),
            addressFields: {
                StreetName: {
                    name: `${this.addressType}AddressStreetName`,
                    label: this.$t('form.fields.streetName.label'),
                    icon: null,
                    validationRules: this.store.getters.fieldValidationRules(`${this.addressType}AddressStreetName`),
                    validationValue: null,
                    errorMessage: this.$t('form.fields.streetName.errorMessage'),
                },
                BuildingName: {
                    name: `${this.addressType}AddressBuildingName`,
                    label: this.$t('form.fields.address.labels.buildingNameOrNumber'),
                    icon: null,
                    validationRules: this.store.getters.fieldValidationRules(`${this.addressType}AddressBuildingName`),
                    validationValue: null,
                    errorMessage: this.$t('form.fields.buildingNameOrNumber.errorMessage'),
                },
                BuildingNumber: {
                    name: `${this.addressType}AddressBuildingNumber`,
                    label: this.$t('form.fields.address.labels.buildingNumber'),
                    icon: null,
                    validationRules: this.store.getters.fieldValidationRules(`${this.addressType}AddressBuildingNumber`),
                    validationValue: null,
                    errorMessage: this.$t('form.fields.buildingNumber.errorMessage'),
                },
                City: {
                    name: `${this.addressType}AddressCity`,
                    label: this.$t('form.fields.city.label'),
                    icon: null,
                    validationRules: this.store.getters.fieldValidationRules(`${this.addressType}AddressCity`),
                    validationValue: null,
                    errorMessage: this.$t('form.fields.city.errorMessage'),
                },
                Postcode: {
                    name: `${this.addressType}AddressPostcode`,
                    label: this.$t('form.fields.address.labels.postcode'),
                    icon: null,
                    validationRules: this.store.getters.fieldValidationRules(`${this.addressType}AddressPostcode`),
                    validationValue: null,
                    errorMessage: this.$t('form.fields.postcode.errorMessage'),
                },
            },
        }
    },
    computed: {
        postcodeValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressPostcode`)
        },
        buildingNameValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressBuildingName`)
        },
        buildingNumberValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressBuildingNumber`)
        },
        streetNameValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressStreetName`)
        },
        cityValue: function () {
            return this.store.getters.fieldValue(`${this.addressType}AddressCity`)
        },
    },
    watch: {
        postcodeValue: function (value) {
            this.addressFields.Postcode.validationValue = value
        },
        buildingNameValue: function (value) {
            this.addressFields.BuildingName.validationValue = value
        },
        buildingNumberValue: function (value) {
            this.addressFields.BuildingNumber.validationValue = value
        },
        streetNameValue: function (value) {
            this.addressFields.StreetName.validationValue = value
        },
        cityValue: function (value) {
            this.addressFields.City.validationValue = value
        },
    }
}
</script>
